/**
 * Constantes, helpers e tipos compartilhados pelos sub-componentes da tela
 * de perfil do aluno (`app/(personal)/students/[id].tsx`).
 *
 * Extraído do arquivo monolítico original (982 linhas) durante o
 * refactor [AÇ-016]. Cada helper aqui é puro — sem side effects, sem
 * dependência de hooks. Pode ser importado em qualquer ponto sem custo.
 *
 * Decisões:
 *   - `EditForm` armazena tudo como string porque os TextInput do form
 *     trabalham com string. Conversão pra number/null acontece só no
 *     `handleSave` do orquestrador.
 *   - `studentToForm` é a única fonte de verdade pro mapeamento
 *     Student → EditForm. Se mudar campo do form, mexer aqui.
 */

import { Student } from '@/lib/types';

export const SEX_LABEL: Record<string, string> = {
  M: 'Masculino',
  F: 'Feminino',
  O: 'Outro',
};

export const GOAL_LABEL: Record<string, string> = {
  hypertrophy: 'Hipertrofia',
  weight_loss: 'Emagrecimento',
  conditioning: 'Condicionamento',
  health: 'Saúde e bem-estar',
};

export const LEVEL_LABEL: Record<string, string> = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
};

export const SEX_OPTIONS = [
  { value: 'M', label: 'M' },
  { value: 'F', label: 'F' },
  { value: 'O', label: 'Outro' },
] as const;

/** Calcula idade em anos a partir de uma data ISO (ou retorna "—" se null). */
export function calcAge(birthDate: string | null): string {
  if (!birthDate) return '—';
  const diff = Date.now() - new Date(birthDate).getTime();
  return `${Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000))} anos`;
}

/**
 * Dada a data de pagamento (`paidAt`) e o dia de vencimento do aluno
 * (`dueDay`, 1–28), retorna o vencimento ISO daquele mês.
 *
 * Retorna `undefined` se não houver `dueDay` definido — usado pra
 * sinalizar "pagamento avulso, sem vencimento de referência".
 */
export function computeDueDate(paidAt: string, dueDay: number | null): string | undefined {
  if (!dueDay) return undefined;
  const d = new Date(paidAt);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(dueDay).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Dias de atraso entre `paidAt` e `dueDate`. Retorna null se pagou
 * em dia ou se não há vencimento. Usado pra colorir o badge na lista
 * de pagamentos (verde = em dia, vermelho = atrasado).
 */
export function getDaysLate(paidAt: string, dueDate: string | null): number | null {
  if (!dueDate) return null;
  const paid = new Date(paidAt);
  const due = new Date(dueDate + 'T23:59:59');
  const diff = Math.floor((paid.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}

export interface EditForm {
  full_name: string;
  email: string;
  phone: string;
  goal: string;
  birth_date: string;
  height_cm: string;
  weight_kg: string;
  sex: 'M' | 'F' | 'O' | null;
  health_notes: string;
  trainer_notes: string;
  monthly_fee: string;
  due_day: string;
}

/** Converte um Student do banco em EditForm pronto pra TextInput. */
export function studentToForm(s: Student): EditForm {
  return {
    full_name: s.full_name ?? '',
    email: s.email,
    phone: s.phone ?? '',
    goal: s.goal ?? '',
    birth_date: s.birth_date ?? '',
    height_cm: s.height_cm != null ? String(s.height_cm) : '',
    weight_kg: s.weight_kg != null ? String(s.weight_kg) : '',
    sex: s.sex ?? null,
    health_notes: s.health_notes ?? '',
    trainer_notes: s.trainer_notes ?? '',
    monthly_fee: s.monthly_fee != null ? String(s.monthly_fee) : '',
    due_day: s.due_day != null ? String(s.due_day) : '10',
  };
}
